/* @jsx jsx */
/** @jsxfrag */

import React, { useRef, useCallback, useState } from 'react';
import { jsx } from '@emotion/react';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { formStyles, logoContainerStyles, formContainerStyles, formInnerStyles, buttonWrapperStyles } from './styles';
import { commonTextStyles } from '../../styles/text';
import { CancelIconButton } from '../CancelIconButton/CancelIconButton';
import { SubmitButton } from '../SubmitButton/SubmitButton';
import { AtlassianIntelligenceLogo } from '../AtlassianIntelligenceLogo/AtlassianIntelligenceLogo';
import { useContainerBreakpoints } from '../../hooks/use-container-breakpoints';
import { PromptEditorWrapper } from '../PromptEditorWrapper/PromptEditorWrapper';
import { PromptMultilineInput } from '../PromptMultilineInput/PromptMultilineInput';
export const PromptForm = ({
  onFormSubmit,
  onCancel,
  value = '',
  showButtons = true,
  tag,
  autoFocus = true,
  onInputChange,
  onADFChange,
  onInputFocus,
  onInputBlur,
  placeholder,
  ariaLabel,
  focusInputRef,
  clearInputRef,
  showLogo = true,
  onInputKeyDown,
  adfValue,
  enableLinks,
  PromptEditor,
  inputControlsLayout = 'row'
}) => {
  const {
    ref,
    breakpoint
  } = useContainerBreakpoints();
  const blurInputRef = useRef(undefined);
  const [hasMutipleRows, setHasMutipleRows] = useState(false);
  const [buttonWidth, setButtonWidth] = useState(0);
  const [isComposing, setIsComposing] = useState(false);
  const handleSubmit = event => {
    event.preventDefault();
    event.stopPropagation();
    onFormSubmit(value);
  };
  const handleSetButtonWidth = useCallback(el => {
    if (el) {
      setButtonWidth(el.getBoundingClientRect().width);
    }
  }, [setButtonWidth]);
  const handleOnCompositionStart = useCallback(() => {
    setIsComposing(true);
  }, []);
  const handleOnCompositionEnd = useCallback(() => {
    setIsComposing(false);
  }, []);
  const handleKeyDown = React.useCallback(event => {
    if (event.key === 'Enter') {
      var _target$getAttribute;
      event.stopPropagation();
      const target = event.target;
      if (isComposing) {
        return;
      }

      // Handles a case where the user presses enter if refinement tag's close button or the
      // cancel button is focused
      if (['close-button-refinement-tag', 'cancel-icon-button', 'cancel-esc-button'].includes((_target$getAttribute = target.getAttribute('data-testid')) !== null && _target$getAttribute !== void 0 ? _target$getAttribute : '')) {
        return;
      }
      // Handles every other case that is not a "shift + enter"
      if (!event.shiftKey) {
        event.preventDefault();
        onFormSubmit(value);
      }
    }
  }, [onFormSubmit, value, isComposing]);
  const usePromptEditor = getBooleanFF('platform.editor.ai-lightweight-editor-prompt_f92x1') && !!PromptEditor;
  const onADFChange_ = React.useCallback(input => {
    if (onADFChange) {
      onADFChange(input);
    }
  }, [onADFChange]);
  const onInputChange_ = React.useCallback(value => {
    if (onInputChange) {
      onInputChange(value);
    }
  }, [onInputChange]);
  return jsx("div", {
    // ED-19496: We avoid propagating enter keypresses
    // since products like Bitbucket might have parent forms
    // that listen for keydown events to trigger form submission
    onKeyDown: handleKeyDown,
    css: formContainerStyles,
    ref: ref
  }, showLogo && jsx("div", {
    css: logoContainerStyles(breakpoint)
  }, jsx(AtlassianIntelligenceLogo, null)), jsx("form", {
    "data-layout": inputControlsLayout,
    css: formStyles(breakpoint),
    onSubmit: handleSubmit
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: "ai-modal-prompt-form",
    "data-testid": "ai-modal-prompt-form"
  }, tag, jsx("div", {
    "data-multiline": hasMutipleRows,
    css: [commonTextStyles, formInnerStyles]
  }, usePromptEditor && jsx(PromptEditorWrapper, {
    PromptEditor: PromptEditor,
    focusInputRef: focusInputRef,
    blurInputRef: blurInputRef,
    clearInputRef: clearInputRef,
    autoFocus: autoFocus,
    defaultValue: adfValue,
    onInputChange: onInputChange_,
    onADFChange: onADFChange_,
    placeholder: placeholder,
    onFocus: onInputFocus,
    onBlur: onInputBlur,
    onKeyDown: onInputKeyDown,
    onCompositionStart: handleOnCompositionStart,
    onCompositionEnd: handleOnCompositionEnd,
    enableLinks: enableLinks
  }), !usePromptEditor && jsx(PromptMultilineInput, {
    focusInputRef: focusInputRef,
    blurInputRef: blurInputRef,
    value: value,
    autoFocus: autoFocus,
    onInputChange: onInputChange_,
    placeholder: placeholder,
    "aria-label": ariaLabel || placeholder,
    onFocus: onInputFocus,
    onBlur: onInputBlur,
    buttonWidth: buttonWidth,
    hasMultipleRows: hasMutipleRows,
    setHasMultipleRows: setHasMutipleRows,
    onKeyDown: onInputKeyDown,
    onCompositionStart: handleOnCompositionStart,
    onCompositionEnd: handleOnCompositionEnd
  }), showButtons && jsx("div", {
    "data-multiline": hasMutipleRows,
    css: [buttonWrapperStyles],
    ref: handleSetButtonWidth
  }, jsx(SubmitButton, {
    disabled: !value
  }), jsx(CancelIconButton, {
    onCancel: () => {
      var _blurInputRef$current;
      onCancel();
      (_blurInputRef$current = blurInputRef.current) === null || _blurInputRef$current === void 0 ? void 0 : _blurInputRef$current.call(blurInputRef);
    }
  })))));
};